import React, { useState, useMemo } from 'react'
import { DataTable } from './data-table';
import { columns } from './columns';
import { useJobs } from './usejobs';
import { Loader } from '../../../components/base/Loader';
import { formatScheduledDate } from '../../../lib/utils';

const CombinedServiceManagement = () => {
  const { jobs, loading, error } = useJobs()
  const [preset, setPreset] = useState({ field: "All Jobs", value: "" })


  const filteredData = useMemo(() => {
    if (!jobs) return []
    if (preset.field === "All Jobs") return jobs
    if (preset.field === "Job Status") return jobs.filter(job => job.jobStatus === preset.value)
    if (preset.field === "Payout Status") return jobs.filter(job => job.ShovelerInfo?.[0]?.PayoutStatus === preset.value)
    if (preset.field === "Payment Method") return jobs.filter(job => job.paymentInfo?.method === preset.value)
    if (preset.field === "Shoveller Action") return jobs.filter(job => job.ShovelerInfo?.[0]?.shovellerAction === preset.value)
    if (preset.field === "House Owner Action") return jobs.filter(job => job.ShovelerInfo?.[0]?.houseOwnerAction === preset.value)
    return jobs
  }, [jobs, preset])

  const tableColumns = useMemo(() => {
    if (preset.field === "All Jobs") return columns
    if (preset.field === "Job Status") return columns.filter(col => ['jobStatus', 'homeOwner', 'shoveller', 'postDate', 'actions'].includes(col.id))
    if (preset.field === "Payout Status") return columns.filter(col => ['jobStatus', 'homeOwner', 'shoveller', 'payoutStatus', 'actions'].includes(col.id))
    if (preset.field === "Payment Method") return columns.filter(col => ['jobStatus', 'homeOwner', 'price', 'paymentMethodIds', 'paymentStatus', 'actions'].includes(col.id))
    if (preset.field === "Shoveller Action") return columns.filter(col => ['jobStatus', 'homeOwner', 'shoveller', 'shovellerJobStatus', 'actions'].includes(col.id))
    if (preset.field === "House Owner Action") return columns.filter(col => ['jobStatus', 'homeOwner', 'shoveller', 'homeownerJobStatus', 'actions'].includes(col.id))
    return columns
  }, [preset])

  const handleExportCSV = (allJobs) => {
    if (!allJobs.length) return
    const exportFields = [
      'jobStatus', 'firstName', 'lastName', 'userName', 'email', 'phone', 'address', 'city', 'province', 'country', 'postalCode',
      'shovellerFirstName', 'shovellerLastName', 'shovellerUserName', 'shovellerAction', 'houseOwnerAction', 'shovellerCompletedDate', 'houseOwnerCompletedDate', 'payoutStatus', 'requestedTime',
      'service', 'serviceInstruction', 'serviceEquipment',
      'locationType', 'longitude', 'latitude',
      'isRequestedForCancel', 'isRequestedForVolunteer',
      'scheduledHour', 'scheduledMinute', 'scheduledPeriod', 'scheduledDate', 'isScheduled', 'completionTime',
      'paymentInitialization', 'amount', 'paymentStatus', 'paymentMethod',
      'stripeSessionId', 'paymentIntentId',
      'jobRating', 'houseOwnerFeedback', 'jobRatingShoveler', 'ShovelerFeedback',
      'authorizationDate', 'createdAt', 'updatedAt', 'referredBy'
    ]
    const headers = exportFields
    console.log("all jobs", allJobs)
    const csvContent = [
      headers.join(','),
      ...allJobs.map(job =>
        exportFields.map(field => {
          if (field === 'firstName') return job.houseOwnerId.firstName || 'N/A'
          if (field === 'lastName') return job.houseOwnerId.lastName || 'N/A'
          if (field === 'userName') return job.houseOwnerId.userName || 'N/A'
          if (field === 'email') return job.houseOwnerId.email || 'N/A'
          if (field === 'phone') return job.houseOwnerId.phone || 'N/A'
          if (field === 'address') return job.houseOwnerId.address || 'N/A'
          if (field === 'city') return job.houseOwnerId.city || 'N/A'
          if (field === 'province') return job.houseOwnerId.province || 'N/A'
          if (field === 'country') return job.houseOwnerId.country || 'N/A'
          if (field === 'postalCode') return job.houseOwnerId.postalCode || 'N/A'
          if (field === 'shovellerFirstName') return job.ShovelerInfo?.[0]?.ShovelerId?.firstName || 'N/A'
          if (field === 'shovellerLastName') return job.ShovelerInfo?.[0]?.ShovelerId?.lastName || 'N/A'
          if (field === 'shovellerUserName') return job.ShovelerInfo?.[0]?.ShovelerId?.userName || 'N/A'
          if (field === 'shovellerAction') return job.ShovelerInfo?.[0]?.shovellerAction || 'N/A'
          if (field === 'houseOwnerAction') return job.ShovelerInfo?.[0]?.houseOwnerAction || 'N/A'
          if (field === 'shovellerCompletedDate') return job.ShovelerInfo?.[0]?.shovellerCompletedDate ? new Date(job.ShovelerInfo[0].shovellerCompletedDate).toISOString() : 'N/A'
          if (field === 'houseOwnerCompletedDate') return job.ShovelerInfo?.[0]?.houseOwnerCompletedDate ? new Date(job.ShovelerInfo[0].houseOwnerCompletedDate).toISOString() : 'N/A'
          if (field === 'payoutStatus') return job.ShovelerInfo?.[0]?.PayoutStatus || 'N/A'
          if (field === 'requestedTime') return job.ShovelerInfo?.[0]?.requestedTime ? new Date(job.ShovelerInfo[0].requestedTime).toISOString() : 'N/A'
          if (field === 'service') return job.serviceDetail?.service || 'N/A'
          if (field === 'serviceInstruction') return (job.serviceDetail?.serviceInstruction || []).join('; ') || 'N/A'
          if (field === 'serviceEquipment') return (job.serviceDetail?.serviceEquipment || []).join('; ') || 'N/A'
          if (field === 'locationType') return job.location?.type || 'N/A'
          if (field === 'longitude') return job.location?.coordinates?.[0]?.toString() || 'N/A'
          if (field === 'latitude') return job.location?.coordinates?.[1]?.toString() || 'N/A'
          if (field === 'isRequestedForCancel') return job.isRequestedForCancel?.toString() || 'N/A'
          if (field === 'isRequestedForVolunteer') return job.isRequestedForVolunteer?.toString() || 'N/A'
          if (field === 'scheduledHour') return job.scheduledTime?.hour || 'N/A'
          if (field === 'scheduledMinute') return job.scheduledTime?.minute || 'N/A'
          if (field === 'scheduledPeriod') return job.scheduledTime?.period || 'N/A'
          if (field === 'scheduledDate') return job.scheduledDate ? formatScheduledDate(job.scheduledDate) : 'N/A'
          if (field === 'isScheduled') return job.isScheduled?.toString() || 'N/A'
          if (field === 'completionTime') return job.completionTime || 'N/A'
          if (field === 'paymentInitialization') return job.paymentInfo?.initialization || 'N/A'
          if (field === 'amount') return job.paymentInfo?.amount ? (job.paymentInfo.amount / 100).toString() : 'N/A'
          if (field === 'paymentStatus') return job.paymentInfo?.status || 'N/A'
          if (field === 'paymentMethod') return job.paymentInfo?.method || 'N/A'
          if (field === 'stripeSessionId') return job.stripeSessionId || 'N/A'
          if (field === 'paymentIntentId') return job.paymentIntentId || 'N/A'
          if (field === 'jobRating') return job.jobRating?.toString() || 'N/A'
          if (field === 'houseOwnerFeedback') return job.houseOwnerFeedback || 'N/A'
          if (field === 'jobRatingShoveler') return job.jobRatingShoveler?.toString() || 'N/A'
          if (field === 'ShovelerFeedback') return job.ShovelerFeedback || 'N/A'
          if (field === 'authorizationDate') return job.authorizationDate ? new Date(job.authorizationDate).toISOString() : 'N/A'
          if (field === 'createdAt') return job.createdAt ? new Date(job.createdAt).toISOString() : 'N/A'
          if (field === 'updatedAt') return job.updatedAt ? new Date(job.updatedAt).toISOString() : 'N/A'
          if (field === 'referredBy') return job.referredBy || 'N/A'
          return job[field] || 'N/A'
        }).join(',')
      )
    ].join('\n')
    console.log("csv", csvContent)
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'all-jobs.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return <Loader />;
  }
  if (error) {
    console.error(error)
  }

  return (
    <div className="container mx-auto py-10">
      <DataTable columns={tableColumns} data={filteredData} allJobs={jobs || []} preset={preset} onPresetChange={setPreset} onExportCSV={handleExportCSV} />
    </div>
  )
}

export default CombinedServiceManagement
// export { showCancelModal, setShowCancelModal, handleCapture, handleManualCaptureClick };
